import React from 'react'
import type { ActivePage } from './MainMenu'
import type { BottomNavKey } from './BottomNav'
import { Gamepad2, Users, Shield, Lock, Trophy } from 'lucide-react'
import { useAccount } from 'wagmi'
import { usePlayerStatus } from '../hooks/useOGBToken'
import { playSoundSafe } from '../utils/audio'

interface GamesProps {
  onNavigateToPage?: (page: ActivePage) => void
  onNavClick: (nav: BottomNavKey) => void
  activeNav: BottomNavKey
  onOpenSuggestion: () => void
  onOpenSettings: () => void
  onOpenInventory: () => void
}

// AGC: Games page component for selecting game modes
// AGC: Follows Profile.tsx design patterns for Base App consistency
export const Games: React.FC<GamesProps> = ({ 
  onNavigateToPage,
}) => {
  const { address, isConnected } = useAccount()
  const { canBattle: canPlayBattle } = usePlayerStatus(address)

  const sharedTopPaddingStyle = { paddingTop: 'calc(env(safe-area-inset-top, 0px) + 72px)' }

  const gameModes = [
    {
      id: 'pve' as const,
      icon: Shield,
      label: 'PVE',
      title: 'Quick Battle',
      subtitle: 'Battle AI bots instantly',
      description: '50 OGB entry',
      color: 'from-blue-500 to-cyan-500',
      borderColor: 'border-blue-400/40',
      bgColor: 'bg-blue-600/20',
      hoverColor: 'hover:bg-blue-600/30',
      textColor: 'text-blue-300',
      disabled: !isConnected || !canPlayBattle,
      onClick: () => onNavigateToPage && onNavigateToPage('quickBattle'),
    },
    {
      id: 'pvp' as const,
      icon: Users,
      label: 'PVP',
      title: 'Player vs Player',
      subtitle: 'Queue against real players',
      description: '50 OGB entry',
      color: 'from-orange-500 to-red-500',
      borderColor: 'border-orange-400/40',
      bgColor: 'bg-orange-600/20',
      hoverColor: 'hover:bg-orange-600/30',
      textColor: 'text-orange-300',
      disabled: !isConnected || !canPlayBattle,
      onClick: () => onNavigateToPage && onNavigateToPage('p2pBattle'),
    },
    {
      id: 'tournaments' as const,
      icon: Trophy,
      label: 'TOURNAMENTS',
      title: 'Tournaments',
      subtitle: 'Join round-robin tournaments',
      description: '50 OGB entry',
      color: 'from-yellow-500 to-orange-500',
      borderColor: 'border-yellow-400/40',
      bgColor: 'bg-yellow-600/20',
      hoverColor: 'hover:bg-yellow-600/30',
      textColor: 'text-yellow-300',
      disabled: !isConnected || !canPlayBattle,
      onClick: () => onNavigateToPage && onNavigateToPage('tournament'),
    },
    {
      id: 'private' as const,
      icon: Lock,
      label: 'PRIVATE GAME',
      title: 'Private Match',
      subtitle: 'Invite friends to battle',
      description: 'Coming soon',
      color: 'from-purple-500 to-pink-500',
      borderColor: 'border-purple-400/40',
      bgColor: 'bg-purple-600/20',
      hoverColor: 'hover:bg-purple-600/30',
      textColor: 'text-purple-300',
      disabled: true,
      onClick: () => {
        alert('Private game feature coming soon!')
      },
    },
  ]

  return (
    <main
      className="pb-[88px] px-3 text-white flex flex-col gap-1.5"
      style={sharedTopPaddingStyle}
    >
      {/* AGC: Hero Header (No Card) - Following Profile.tsx pattern */}
      <section className="pb-2 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500">GAME MODES</p>
            <h1 className="text-2xl font-bold leading-tight text-white">Select Battle</h1>
            <p className="text-[11px] text-slate-400">Choose your game mode</p>
          </div>
          <div className="text-[10px] px-2.5 py-1 rounded-lg bg-white/5 border border-white/10">
            <Gamepad2 className="w-4 h-4 text-slate-400" />
          </div>
        </div>
      </section>

      {/* AGC: Game Mode Buttons - Compact grid layout */}
      <section className="flex flex-col gap-2">
        {gameModes.map((mode) => {
          const Icon = mode.icon
          return (
            <button
              key={mode.id}
              data-tour={mode.id === 'pve' ? 'battle-quick-battle' : mode.id === 'pvp' ? 'battle-player-vs-player' : mode.id === 'tournaments' ? 'battle-tournaments' : 'battle-private-room'}
              onClick={() => {
                if (!mode.disabled) {
                  playSoundSafe('/sounds/click.mp3', 0.3)
                }
                mode.onClick()
              }}
              disabled={mode.disabled}
              className={`
                w-full rounded-xl border ${mode.borderColor} ${mode.bgColor} px-4 py-4 text-left
                ${mode.disabled ? 'opacity-50 cursor-not-allowed' : `${mode.hoverColor} transition-all hover:scale-[1.01]`}
                shadow-[0_8px_20px_rgba(0,0,0,0.45)]
              `}
            >
              <div className="flex items-center gap-3">
                {/* AGC: Icon with gradient background */}
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${mode.color} flex items-center justify-center shadow-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                
                {/* AGC: Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className={`text-sm font-semibold ${mode.textColor}`}>{mode.title}</p>
                    {mode.disabled && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-400 uppercase">
                        {mode.id === 'private' ? 'Soon' : 'Locked'}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-300 mb-0.5">{mode.subtitle}</p>
                  <p className="text-[10px] text-slate-500">{mode.description}</p>
                </div>

                {/* AGC: Arrow indicator */}
                {!mode.disabled && (
                  <div className="text-slate-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </div>
            </button>
          )
        })}
      </section>

      {/* AGC: Info Section - Compact design */}
      {(!isConnected || !canPlayBattle) && (
        <section className="bg-slate-950/60 border border-slate-900 rounded-2xl p-2.5 shadow-[0_12px_32px_rgba(0,0,0,0.4)]">
          <div className="text-center py-2">
            <p className="text-[11px] text-slate-400 mb-1">
              {!isConnected ? 'Connect wallet to start battling' : 'Insufficient OGB balance (50 OGB required)'}
            </p>
          </div>
        </section>
      )}
    </main>
  )
}

